<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.adminCP.workflow_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="openAddWorkflow">
                {{ $t('labels.workflowModule.add') }}
            </hts-form-button>
        </div>

        <div class="mb-3">
            <hts-form-button
                v-if="workflowMultiSelected?.length > 1"
                color="main"
                icon-name="hi-x-circle-icon"
                icon-position="right"
                type="submit"
                @click="deleteWorkflows = true">
                {{ $t('labels.businessModule.deleteMulti') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="tables"
            :headers="headers"
            :server="apiGetWorkflow"
            :auto-size="autoSizeTable"
            :use-archive="useWorkflowArchive"
            :use-restore="useWorkflowRestore"
            use-selection
            store-name="workflowDataTable"
            store-key="Workflow"
            @selection-changed="onSelectionChanged" />

        <add-workflow
            ref="addWorkflow"
            @refresh-tables="refreshTables()" />

        <edit-workflow
            ref="editWorkflow"
            @refresh-tables="refreshTables()" />

        <delete-workflow
            :show="deleteWorkflow"
            :workflow="workflowSelectedDelete"
            @refresh-tables="refreshTables()"
            @close="closeDelete" />

        <delete-workflow
            :show="deleteWorkflows"
            :workflow="workflowIdMultiSelected"
            @refresh-tables="refreshTables()"
            @reset-task-selecteds="resetWorkflowSelecteds()"
            @close="closeDeletes" />

        <detail-workflow ref="detailWorkflow" />
    </div>
</template>
<script lang="ts" setup>
import AddWorkflow from './-components/add.vue'
import DeleteWorkflow from './-components/delete.vue'
import DetailWorkflow from './-components/detail.vue'
import EditWorkflow from './-components/edit.vue'
import { IWorkflow } from './-imports/form'
import layoutPermissions from '~/data/permissions/layout-permissions'

const apiGetWorkflow = $urlAdmin.workflow['get-list']
const { $formatters } = useNuxtApp()

const tables = ref()
const addWorkflow = ref()
const editWorkflow = ref()
const detailWorkflow = ref()

const viewWorkflow = ref<Boolean>(false)
const nodeTypes = ref<Array<{ [key: string]: any }>>([])
const deleteWorkflow = ref<Boolean>(false)
const deleteWorkflows = ref<Boolean>(false)
const workflowSelectedEdit = ref<IWorkflow>()
const workflowSelectedDelete = ref<{ [key: string]: any }>({})
const workflowSelectedView = ref<IWorkflow>()
const workflowMultiSelected = ref<Array<IWorkflow> | null>()

onBeforeMount(() => {
  getNodeTypesList()
})

const openAddWorkflow = () => {
  if (addWorkflow.value) {
    addWorkflow.value.open()
  }
}

const handleDelete = (deleteOptions: any) => {
  if (deleteOptions && deleteOptions._id) {
    workflowSelectedDelete.value = {
      selectedItems: [deleteOptions._id]
    }
  } else {
    workflowSelectedDelete.value = deleteOptions
  }
  deleteWorkflow.value = true
}

const closeDelete = () => {
  workflowSelectedDelete.value = {}
  deleteWorkflow.value = false
}

const closeDeletes = () => {
  workflowMultiSelected.value = null
  deleteWorkflows.value = false
}

const resetWorkflowSelecteds = () => {
  workflowMultiSelected.value = null
}

const onSelectionChanged = (workflow: Array<IWorkflow> | null) => {
  workflowMultiSelected.value = workflow
}

const workflowIdMultiSelected = computed(() => {
  if (!workflowMultiSelected.value || !Array.isArray(workflowMultiSelected.value)) {
    return []
  }
  return workflowMultiSelected.value.map((item) => item._id)
})

const handleEdit = (workflow: IWorkflow) => {
  if (editWorkflow.value) {
    editWorkflow.value.open(workflow._id)
  }
}

const handleViewDetail = (workflow: IWorkflow) => {
  if (detailWorkflow.value) {
    detailWorkflow.value.open(workflow._id)
  }
}

const headers = [
  {
    field: 'name',
    sortable: true,
    headerName: $t('labels.workflowModule.name'),
    filter: 'agTextColumnFilter',
    minWidth: 150
  },
  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    filter: 'agTextColumnFilter',
    minWidth: 200,
    valueGetter: (params: any) => {
      const { transportation } = params.data
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'positions',
    sortable: true,
    headerName: $t('labels.workflow'),
    minWidth: 200,
    valueFormatter: (params: any) => {
      if (params.data?.positions && Array.isArray(params.data?.positions)) {
        const pluckData = $lodash.map(params.data?.positions, 'nodeTypeName')
        return $lodash.join(pluckData, ' >> ')
      }
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    minWidth: 180,
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    minWidth: 180,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    popup: true,
    suppressRowTransform: true,
    suppressMenu: true,
    resizable: false,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'Actions',
    cellRendererParams: {
      storeKey: 'workflowDataTable',
      storeName: 'Workflow',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          click: handleViewDetail,
          isAll: true
        },
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.adminCP.workflow_edit),
          click: handleEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          isArchive: true,
          permissions: checkPermission(layoutPermissions.adminCP.workflow_delete),
          click: handleDelete
        }
      ]
    }
  }
]

const getNodeTypesList = () => {
  const api = $urlAdmin.node.getNodeTypes
  $api(api)
    .then((data) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (responseData.success) {
        nodeTypes.value = responseData.data.map((item: any) => {
          item = {
            ...item,
            label: $t(item.label)
          }
          return item
        })
      }
    })
    .catch((error: any) => error)
}

const refreshTables = () => {
  if (tables.value) {
    tables.value.refresh()
  }
}

const autoSizeTable = {
  active: false,
  skipHeader: false
}

const useWorkflowArchive = computed(() => {
  return {
    api: $urlAdmin.workflow.archive,
    permissions: checkPermission(layoutPermissions.adminCP.workflow_archive)
  }
})
const useWorkflowRestore = computed(() => {
  return {
    api: $urlAdmin.workflow.restore,
    permissions: true
  }
})
</script>
